import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import bootstrap from 'bootstrap';
import Handlebars from 'handlebars';
import React from 'react';
import Global from 'models/Global';
import BootstrapData from 'models/BootstrapData';
import RenderedView from 'views/RenderedView';
import SectionRenderMixin from 'web-common/editor-templates/scripts/SectionRenderMixin';
import config from 'config';
import { renderReact } from 'utils/reactRoot';
import SectionFactory from '@bizzabo/website-sections';
import FeatureToggleUtils from 'FeatureToggleUtils';
import { onCategoryAllowed, ConsentCategory } from 'utils/consentHelper';
import { AiSection, loadRemoteSectionWithConfig, withLayoutConfig } from '@bizzabo/ai-section-adapter';
import { buildRemoteSectionUrl, buildRemoteSectionConfigUrl } from 'utils/remoteSectionUrls';
import { ensureThemeTokens } from 'utils/eventThemeTokens';
import { sectionWrapperClass } from 'web-common/scripts/utils/sectionWrapperClass';

// DIESEL-183: map each legacy Backbone content section type to its React port's
// registry key in @bizzabo/website-sections. Consumers REMAP to the react* key
// when reactContentSections is on — safe-by-default, since the raw types aren't
// registered, so without the remap SectionFactory.isExist() is false and the
// Backbone renderer is used. Keep in sync with the SSR map in HomeService.js.
const CONTENT_TYPE_TO_REACT = { multiElement: 'reactMultiElement', html: 'reactHtml', buttons: 'reactButtons' };

var SectionView = RenderedView.extend({

    events: {

    },

    initialize: function(options) {
        this.subViews = [];
        this.findTemplate();
    },

    remove: function() {
        this.isRemoved = true;
        this.removeSubviews();
        return RenderedView.prototype.remove.call(this);
    },

    removeSubviews: function() {
        _.each(this.subViews, function(subView) {
            subView.remove();
        });
        this.subViews = [];
    },

    findTemplate: function() {
        const { template = '' } = BootstrapData('bootstrap-data-siteConf') || {};
        this.template = Handlebars.sectionsTemplates[this.model.get('template') + '/' + this.model.get('type')];
        if (!this.template) {
            this.template = Handlebars.sectionsTemplates['common' + '/' + this.model.get('type')] || Handlebars.sectionsTemplates[template + '/' + this.model.get('type')];
        }
    },

    shouldReturnReactSection: function(type) {
        const reactSectionExists = SectionFactory.isExist(type);
        return reactSectionExists;
    },

    render: function() {
        let event = Global.get('event');
        event = event ? event.toJSON() : {};
        const section = this.model.toJSON();
        const type = section.type;
        // DIESEL-172: route the `form` section to the React `reactForm` renderer,
        // gated on the reactFormSection flag. QA URL overrides win over the flag:
        // ?backboneForm forces the Backbone form, ?reactForm forces the React form.
        const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const useReactForm =
            !search?.has('backboneForm') &&
            (search?.has('reactForm') || FeatureToggleUtils.isFeatureEnabled('reactFormSection'));
        // DIESEL-183: the legacy content sections opt into their React ports by
        // REMAPPING the section type to the react* registry key when
        // reactContentSections is on (safe-by-default — the raw types aren't
        // registered). QA URL overrides win over the flag: ?backboneContent forces
        // Backbone, ?reactContent forces React.
        const useReactContent =
            !search?.has('backboneContent') &&
            (search?.has('reactContent') || FeatureToggleUtils.isFeatureEnabled('reactContentSections'));
        const contentReactType = CONTENT_TYPE_TO_REACT[type];
        const resolvedType =
            useReactForm && type === 'form' ? 'reactForm'
                : useReactContent && contentReactType ? contentReactType
                    : type;
        const isReactSection = this.shouldReturnReactSection(resolvedType);
        // AI-only section types have no bundled component, so skip the SectionFactory gate.
        const isRemoteSection = !!section.layoutVersionKey && FeatureToggleUtils.isFeatureEnabled('intelligence-poc');
        const registrationProxyEnabled = FeatureToggleUtils.isFeatureEnabled('authReg', 'feature-sso-enable-attendee-sso') || FeatureToggleUtils.isFeatureEnabled('regProxy');
        const flowId = type === 'tickets' ? section?.elements?.find(element => element.type === 'tickets')?.flowId : null;
        // Important: Please check enrichReactSections() in ReactSectionRenderer in the node.js backend to ensure you pass all desired args.
        if (isRemoteSection || isReactSection){
            this.applySectionContainerClasses();
            const bypassCookieConsent = FeatureToggleUtils.isFeatureEnabled('bypass-cookie-consent');
            const onMapsAllowed = bypassCookieConsent ? undefined : (callback) => onCategoryAllowed(ConsentCategory.Targeting, callback);
            const configParams = {...config, FeatureToggleUtils, global: Global.toJSON(), onMapsAllowed};
            const sectionData = {...section, event, registrationProxyEnabled, flowId};
            if (isRemoteSection) {
                // Remote (AI-generated) section. SSR already painted this.el, so fetch the
                // bundle and mount AiSection through renderReact. AiSection renders
                // synchronously (no Suspense/skeleton) into the identical markup, so there
                // is no visible flash — same as the local-section path below, which also
                // client-renders over its SSR markup. On failure, leave the SSR markup so
                // the section stays in its non-interactive state.
                Promise.all([
                    loadRemoteSectionWithConfig(sectionData, {
                        buildUrl: buildRemoteSectionUrl,
                        buildConfigUrl: buildRemoteSectionConfigUrl,
                    }),
                    ensureThemeTokens(event),
                ])
                    .then(([{ RemoteComponent, layoutConfig }]) => {
                        if (this.isRemoved) {
                            return;
                        }
                        renderReact(
                            this.el,
                            <AiSection
                                section={withLayoutConfig(sectionData, layoutConfig)}
                                config={configParams}
                                RemoteComponent={RemoteComponent}
                            />
                        );
                    })
                    .catch(() => {});
            } else {
                const SectionComponent = SectionFactory.create(resolvedType, sectionData, configParams);
                renderReact(this.el, SectionComponent);
            }
        } else {
            RenderedView.prototype.render.call(this, this.template);
        }

        return this;
    },

    // Section customization CSS is scoped to `.{template}-template.section-{rootKey}`.
    // SSR's sectionRender helper puts these classes on the container, but on client-side
    // navigation React sections mount into a bare element and postRender (Backbone only)
    // never runs, so mirror the SSR container here. Generated sections get no template class.
    applySectionContainerClasses: function() {
        this.$el.addClass(sectionWrapperClass(this.model.toJSON()));
    },

    postRender: function() {
        if (!this.$el.hasClass('section-' + this.model.get('rootKey'))) {
            var wrapperDiv = '<div class="' + sectionWrapperClass(this.model.toJSON()) + '">' + this.$el.html() + '</div>';
            this.$el.html(wrapperDiv);
        }
        this.removeSubviews();
        this.renderByType();
        return this;
    },

    renderByType: function() {
        var thisView = this;
        var type = this.model.get('type');
        _.each(this.model.get('elements'), function(element) {
            var functionName = thisView.elementsRender[element.type];
            functionName && thisView[functionName](element);
        });
        return this;
    }
});

_.extend(SectionView.prototype, SectionRenderMixin);

export default SectionView;
